import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const getToken = () => localStorage.getItem("token");

export const getBlogs = createAsyncThunk(
  "blogs/getBlogs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await axios.get(`${API_URL}/blogs${query ? `?${query}` : ""}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch blogs");
    }
  }
);

export const getBlogById = createAsyncThunk(
  "blogs/getBlogById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/blogs/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Blog not found");
    }
  }
);

export const createBlog = createAsyncThunk(
  "blogs/createBlog",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/blogs`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create blog");
    }
  }
);

export const updateBlog = createAsyncThunk(
  "blogs/updateBlog",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${API_URL}/blogs/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update blog");
    }
  }
);

export const deleteBlog = createAsyncThunk(
  "blogs/deleteBlog",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/blogs/${id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete blog");
    }
  }
);

const blogSlice = createSlice({
  name: "blogs",
  initialState: {
    blogs: [],
    currentBlog: null,
    loading: false,
    saving: false,
    error: null,
    total: 0,
    pages: 1,
  },
  reducers: {
    clearCurrentBlog: (state) => { state.currentBlog = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBlogs.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(getBlogs.fulfilled, (state, action) => {
        state.loading = false;
        state.blogs = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.pages = action.payload.pages || 1;
      })
      .addCase(getBlogs.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(getBlogById.pending, (state) => { state.loading = true; state.currentBlog = null; })
      .addCase(getBlogById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlog = action.payload.data || action.payload;
      })
      .addCase(getBlogById.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      .addCase(createBlog.pending, (state) => { state.saving = true; })
      .addCase(createBlog.fulfilled, (state, action) => {
        state.saving = false;
        const blog = action.payload.data || action.payload;
        state.blogs.unshift(blog);
      })
      .addCase(createBlog.rejected, (state, action) => { state.saving = false; state.error = action.payload; })

      .addCase(updateBlog.pending, (state) => { state.saving = true; })
      .addCase(updateBlog.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload.data || action.payload;
        state.blogs = state.blogs.map((b) => b._id === updated._id ? updated : b);
        state.currentBlog = updated;
      })
      .addCase(updateBlog.rejected, (state, action) => { state.saving = false; state.error = action.payload; })

      .addCase(deleteBlog.pending, (state) => { state.loading = true; })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.loading = false;
        state.blogs = state.blogs.filter((b) => b._id !== action.payload);
      })
      .addCase(deleteBlog.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
  },
});

export const { clearCurrentBlog } = blogSlice.actions;
export default blogSlice.reducer;
